import {
    buildAdvancedFiltersJsonLogic,
    type AdvancedFilterDefinition,
} from '@/shared/components/filters/advancedFilters';
import {
    compactJsonLogicConditions,
    isJsonLogicRuleEmpty,
    jsonLogicField,
    type JsonLogicFilter,
    type JsonLogicRule,
} from '@/shared/components/filters/jsonLogic';

import type { ListFilterRule, ListFilterScalarValue, ListFilterValue } from '.';

const normalizeListFilterValue = (value: unknown): ListFilterScalarValue | undefined => {
    if (typeof value === 'string') {
        const normalized = value.trim();

        return normalized || undefined;
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
    }

    return typeof value === 'boolean' ? value : undefined;
};

export const normalizeListFilterValues = (value: ListFilterValue | null | undefined): ListFilterScalarValue[] =>
    (Array.isArray(value) ? value : [value])
        .map(normalizeListFilterValue)
        .filter((item): item is ListFilterScalarValue => item !== undefined);

const buildListFilterCondition = (rule: ListFilterRule, value: ListFilterScalarValue): JsonLogicRule => {
    const variable = jsonLogicField(rule.field);

    switch (rule.operator) {
        case 'contains':
            return { in: [value, variable] };
        case 'notEquals':
            return { '!=': [variable, value] };
        case 'greaterThan':
            return { '>': [variable, value] };
        case 'lessThan':
            return { '<': [variable, value] };
        case 'equals':
        default:
            return { '==': [variable, value] };
    }
};

export const buildListFilterRuleJsonLogic = (rule: ListFilterRule): JsonLogicRule | undefined => {
    const values = normalizeListFilterValues(rule.value);
    const valueConditions = values.map((value) => buildListFilterCondition(rule, value));

    if (valueConditions.length <= 1) {
        return valueConditions[0];
    }

    return compactJsonLogicConditions(valueConditions, rule.valueJoin ?? 'and');
};

export const buildListFilterJsonLogic = (
    rules: ListFilterRule[],
    advancedFilter?: AdvancedFilterDefinition<ListFilterRule> | AdvancedFilterDefinition<ListFilterRule>[],
): JsonLogicFilter => {
    const standardRule = compactJsonLogicConditions(rules.map(buildListFilterRuleJsonLogic));
    const advancedRule = buildAdvancedFiltersJsonLogic(
        Array.isArray(advancedFilter) ? advancedFilter : advancedFilter ? [advancedFilter] : [],
        buildListFilterRuleJsonLogic,
    );

    if (!advancedRule) {
        return standardRule;
    }

    if (isJsonLogicRuleEmpty(standardRule)) {
        return advancedRule as JsonLogicFilter;
    }

    return compactJsonLogicConditions([standardRule, advancedRule]);
};

export const buildListFilterExpression = buildListFilterJsonLogic;
